import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useEffectivePlan } from '../store/accountStore';
import { useBuilderStore } from '../store/builderStore';
import {
  Globe,
  Plus,
  CheckCircle,
  AlertCircle,
  Trash2,
  Copy,
  ExternalLink,
  Crown,
  Lock,
  RefreshCw,
  Loader2,
  Shield,
  Pencil,
  Save,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { hasFeature } from '../constants/planLimits';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const UNLIMITED_CAP = -1;

function isUnlimitedCap(cap: number): boolean {
  return cap === UNLIMITED_CAP;
}

function canAddCustomDomain(used: number, cap: number): boolean {
  return isUnlimitedCap(cap) || used < cap;
}

// Lifecycle states from the backend (server/services/cfSaas.js).
type DomainStatus = 'pending' | 'active' | 'failed';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

interface VerificationPayload {
  ownership: DnsRecord | null;
  sslValidation: { method?: string; name?: string; value?: string } | null;
}

interface CustomDomainRow {
  id: string;
  hostname: string;
  pageId: string;
  pageName: string | null;
  status: DomainStatus;
  sslStatus: string | null;
  cfHostnameId: string | null;
  lastCheckedAt: string | null;
  verifiedAt: string | null;
  createdAt: string;
  cnameTarget: string;
}

interface ListResponse {
  configured: boolean;
  plan: string;
  cap: number;
  used: number;
  cnameTarget: string;
  domains: CustomDomainRow[];
}

export const CustomDomain: React.FC = () => {
  const { t, i18n } = useTranslation('tracking');
  const { token } = useAuthStore();
  const effectivePlan = useEffectivePlan();
  const { pages } = useBuilderStore();

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ListResponse | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [selectedPageId, setSelectedPageId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verificationByDomain, setVerificationByDomain] = useState<
    Record<string, VerificationPayload>
  >({});
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  // Per-row "edit page" state. Holds the id of the row being edited
  // plus the currently selected pageId in its dropdown so the parent
  // can drive the PATCH call without each card owning its own fetch.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPageId, setEditingPageId] = useState<string>('');
  const [savingReassign, setSavingReassign] = useState(false);

  // Custom domains require Pro+ in the new pricing model. Single source
  // of truth: PLAN_LIMITS.features.customDomain (also enforced server-side
  // in routes/customDomains.js so the gate can't be bypassed by editing
  // the FE). Use workspace owner's plan for team members.
  const isPaid = hasFeature(effectivePlan, 'customDomain');
  const cnameTarget = list?.cnameTarget || 'cs.hitzlink.com';
  const domainCapLabel = list && isUnlimitedCap(list.cap) ? t('domain.unlimited') : list?.cap;
  const canAddDomain = list ? canAddCustomDomain(list.used, list.cap) : false;

  const authHeader = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const refreshList = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/custom-domains`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ListResponse = await res.json();
      setList(data);
    } catch (err) {
      console.error('Load custom domains:', err);
      toast.error(t('domain.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [authHeader, token, t]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  // Auto-poll every 15s for any domain still in `pending`. Stops as
  // soon as everything is active/failed so we don't burn CF API quota.
  useEffect(() => {
    if (!list) return;
    const pending = list.domains.filter((d) => d.status === 'pending');
    if (!pending.length) return;
    const id = setInterval(() => {
      // Refresh the whole list in one go — cheaper than N parallel
      // status calls and the server reads from CF when row is stale.
      void refreshList();
    }, 15_000);
    return () => clearInterval(id);
  }, [list, refreshList]);

  const handleAddDomain = async () => {
    if (!canAddDomain) {
      toast.error(t('domain.toast.capReached'));
      return;
    }
    if (!newDomain.trim() || !selectedPageId) {
      toast.error(t('domain.toast.fillFields'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/custom-domains`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostname: newDomain.trim(), pageId: selectedPageId }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error || t('domain.toast.addFailed'));
        return;
      }
      toast.success(t('domain.toast.added'));
      if (body.verification) {
        setVerificationByDomain((prev) => ({
          ...prev,
          [body.domain.id]: body.verification,
        }));
      }
      setShowAddModal(false);
      setNewDomain('');
      setSelectedPageId('');
      await refreshList();
    } catch (err) {
      console.error(err);
      toast.error(t('domain.toast.addFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshStatus = async (id: string) => {
    setRefreshingId(id);
    try {
      const res = await fetch(`${API_URL}/custom-domains/${id}/status`, {
        headers: authHeader(),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error || t('domain.toast.refreshFailed'));
        return;
      }
      if (body.rateLimited) {
        toast(t('domain.toast.rateLimited'));
      } else if (body.domain.status === 'active') {
        toast.success(t('domain.toast.verified'));
      }
      if (body.verification) {
        setVerificationByDomain((prev) => ({ ...prev, [id]: body.verification }));
      }
      await refreshList();
    } catch (err) {
      console.error(err);
      toast.error(t('domain.toast.refreshFailed'));
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRevalidate = async (id: string) => {
    setRefreshingId(id);
    try {
      const res = await fetch(`${API_URL}/custom-domains/${id}/revalidate`, {
        method: 'POST',
        headers: authHeader(),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error || t('domain.toast.revalidateFailed'));
        return;
      }
      toast.success(t('domain.toast.revalidateQueued'));
      if (body.verification) {
        setVerificationByDomain((prev) => ({ ...prev, [id]: body.verification }));
      }
      await refreshList();
    } catch (err) {
      console.error(err);
      toast.error(t('domain.toast.revalidateFailed'));
    } finally {
      setRefreshingId(null);
    }
  };

  const handleStartEditPage = (id: string, currentPageId: string) => {
    setEditingId(id);
    setEditingPageId(currentPageId);
  };

  const handleCancelEditPage = () => {
    setEditingId(null);
    setEditingPageId('');
  };

  const handleSaveReassign = async (id: string) => {
    if (!editingPageId) {
      toast.error(t('domain.toast.fillFields'));
      return;
    }
    setSavingReassign(true);
    try {
      const res = await fetch(`${API_URL}/custom-domains/${id}`, {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId: editingPageId }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error || t('domain.toast.reassignFailed'));
        return;
      }
      if (body.unchanged) {
        toast(t('domain.toast.reassignUnchanged'));
      } else {
        toast.success(t('domain.toast.reassigned'));
      }
      setEditingId(null);
      setEditingPageId('');
      await refreshList();
    } catch (err) {
      console.error(err);
      toast.error(t('domain.toast.reassignFailed'));
    } finally {
      setSavingReassign(false);
    }
  };

  const handleDeleteDomain = async (id: string, hostname: string) => {
    if (!confirm(t('domain.toast.deleteConfirm', { hostname }))) return;
    try {
      const res = await fetch(`${API_URL}/custom-domains/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.error || t('domain.toast.removeFailed'));
        return;
      }
      toast.success(t('domain.toast.removed'));
      setVerificationByDomain((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await refreshList();
    } catch (err) {
      console.error(err);
      toast.error(t('domain.toast.removeFailed'));
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('domain.toast.copied', { label }));
  };

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              {t('domain.title')}
            </h1>
            <p className="text-gray-600 mt-1">
              {isPaid ? t('domain.subtitleConnected') : t('domain.subtitleLocked')}
            </p>
          </div>
          {isPaid && list?.configured && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {t('domain.usage', { used: list.used, cap: domainCapLabel })}
              </span>
              <button
                onClick={() => setShowAddModal(true)}
                disabled={!canAddDomain}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                {t('domain.addButton')}
              </button>
            </div>
          )}
        </div>

        {/* Server not configured */}
        {isPaid && list && !list.configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">
                {t('domain.notConfigured.title')}
              </h3>
              <p className="text-sm text-amber-800">{t('domain.notConfigured.body')}</p>
            </div>
          </div>
        )}

        {/* Pro Required Notice */}
        {!isPaid && (
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-orange-200 rounded-xl p-8 text-center mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('domain.locked.title')}</h3>
            <p className="text-gray-700 mb-6">{t('domain.locked.body')}</p>
            <button
              onClick={() => (window.location.href = '/upgrade')}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all hover:shadow-lg"
            >
              <Crown className="w-5 h-5" />
              {t('domain.locked.cta')}
            </button>
          </div>
        )}

        {/* Domains List */}
        {isPaid && (
          <>
            {loading ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Loader2 className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-spin" />
                <p className="text-sm text-gray-600">{t('domain.loading')}</p>
              </div>
            ) : list && list.domains.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('domain.empty.title')}
                </h3>
                <p className="text-gray-600 mb-6">{t('domain.empty.hint')}</p>
                {list?.configured && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    disabled={!canAddDomain}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    {t('domain.addFirstButton')}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-6">
                {list?.domains.map((domain) => (
                  <DomainCard
                    key={domain.id}
                    domain={domain}
                    cnameTarget={cnameTarget}
                    verification={verificationByDomain[domain.id] || null}
                    refreshing={refreshingId === domain.id}
                    onRefresh={() => handleRefreshStatus(domain.id)}
                    onRevalidate={() => handleRevalidate(domain.id)}
                    onDelete={() => handleDeleteDomain(domain.id, domain.hostname)}
                    onCopy={copyToClipboard}
                    locale={i18n.language}
                    // Reassign UX wiring. Cards stay dumb; parent owns
                    // which row is being edited so only one can edit
                    // at a time.
                    pages={pages.map((p) => ({ id: p.id, name: p.name }))}
                    isEditingPage={editingId === domain.id}
                    editingPageId={editingPageId}
                    savingReassign={savingReassign}
                    onStartEditPage={() => handleStartEditPage(domain.id, domain.pageId)}
                    onCancelEditPage={handleCancelEditPage}
                    onChangeEditingPageId={setEditingPageId}
                    onSaveReassign={() => handleSaveReassign(domain.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Setup Guide */}
        {isPaid && list?.configured && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-gray-900 mb-4">{t('domain.guide.title')}</h3>
            <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
              <li>{t('domain.guide.step1')}</li>
              <li>{t('domain.guide.step2')}</li>
              <li>
                {t('domain.guide.step3Pre')}
                <code className="px-1.5 py-0.5 bg-white rounded text-xs">{cnameTarget}</code>
              </li>
              <li>{t('domain.guide.step3Proxy')}</li>
              <li>{t('domain.guide.step4')}</li>
              <li>
                {t('domain.guide.step5Pre')}
                <code className="px-1.5 py-0.5 bg-white rounded text-xs">{t('domain.guide.step5Code')}</code>
              </li>
            </ol>
          </div>
        )}
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !submitting && setShowAddModal(false)}
        >
          <div
            className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('domain.modal.title')}</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('domain.modal.domainLabel')}
                </label>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder={t('domain.modal.domainPlaceholder')}
                  autoComplete="off"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{t('domain.modal.domainHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('domain.modal.pageLabel')}
                </label>
                <select
                  value={selectedPageId}
                  onChange={(e) => setSelectedPageId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domain.modal.selectPage')}</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {t('domain.modal.cancel')}
              </button>
              <button
                onClick={handleAddDomain}
                disabled={submitting || !canAddDomain || !newDomain.trim() || !selectedPageId}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? t('domain.modal.provisioning') : t('domain.modal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </UserLayout>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Per-domain card with status + DNS records the customer needs to add.
// Lives in this file (not extracted) because it shares enough props
// with the parent to make the prop-drilling cost > the file-split benefit.
// ─────────────────────────────────────────────────────────────────────

interface DomainCardProps {
  domain: CustomDomainRow;
  cnameTarget: string;
  verification: VerificationPayload | null;
  refreshing: boolean;
  onRefresh: () => void;
  onRevalidate: () => void;
  onDelete: () => void;
  onCopy: (text: string, label: string) => void;
  locale: string;
  // Reassign UX. Pages list is the FULL list of the user's pages —
  // we don't filter out the current page here so the dropdown's
  // selected option is always present (otherwise React renders a
  // mismatched value warning on first open).
  pages: Array<{ id: string; name: string }>;
  isEditingPage: boolean;
  editingPageId: string;
  savingReassign: boolean;
  onStartEditPage: () => void;
  onCancelEditPage: () => void;
  onChangeEditingPageId: (id: string) => void;
  onSaveReassign: () => void;
}

const DomainCard: React.FC<DomainCardProps> = ({
  domain,
  cnameTarget,
  verification,
  refreshing,
  onRefresh,
  onRevalidate,
  onDelete,
  onCopy,
  locale,
  pages,
  isEditingPage,
  editingPageId,
  savingReassign,
  onStartEditPage,
  onCancelEditPage,
  onChangeEditingPageId,
  onSaveReassign,
}) => {
  const { t } = useTranslation('tracking');
  const isActive = domain.status === 'active';
  const isFailed = domain.status === 'failed';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-xl font-bold text-gray-900 truncate">{domain.hostname}</h3>
            {isActive ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
                <CheckCircle className="w-3 h-3" />
                {t('domain.card.active')}
              </span>
            ) : isFailed ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">
                <AlertCircle className="w-3 h-3" />
                {t('domain.card.failed')}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-semibold">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t('domain.card.pending')}
              </span>
            )}
            {domain.sslStatus && !isActive && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] uppercase tracking-wide font-medium">
                <Shield className="w-3 h-3" />
                {t('domain.card.ssl', { status: domain.sslStatus })}
              </span>
            )}
          </div>
          {/* "Connected to" line. Click the pencil to switch which
              landing page this domain serves — no DNS change required,
              no CF cert reissue, just a DB pointer flip + cache bust. */}
          {isEditingPage ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-600 whitespace-nowrap">
                {t('domain.card.connectedTo')}
              </span>
              <select
                value={editingPageId}
                onChange={(e) => onChangeEditingPageId(e.target.value)}
                disabled={savingReassign}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">{t('domain.modal.selectPage')}</option>
                {pages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                onClick={onSaveReassign}
                disabled={savingReassign || !editingPageId}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                title={t('domain.card.saveReassign')}
              >
                {savingReassign ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={onCancelEditPage}
                disabled={savingReassign}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                title={t('domain.card.cancelReassign')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-600 flex items-center gap-1.5">
              {t('domain.card.connectedTo')}{' '}
              <span className="font-medium text-gray-900">{domain.pageName || '—'}</span>
              <button
                onClick={onStartEditPage}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title={t('domain.card.changePage')}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {t('domain.card.addedOn', {
              date: new Date(domain.createdAt).toLocaleDateString(locale),
            })}
            {domain.lastCheckedAt && (
              <>
                {' · '}
                {t('domain.card.lastChecked', {
                  time: new Date(domain.lastCheckedAt).toLocaleTimeString(locale),
                })}
              </>
            )}
          </p>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {isActive && (
            <button
              onClick={() => window.open(`https://${domain.hostname}`, '_blank')}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={t('domain.card.openTitle')}
            >
              <ExternalLink className="w-5 h-5" />
            </button>
          )}
          {!isActive && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
              title={t('domain.card.refreshTitle')}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t('domain.card.removeTitle')}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* DNS instructions for non-active rows */}
      {!isActive && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-sm">
              {t('domain.card.instructionsTitle')}
            </h4>
            <p className="text-xs text-gray-600 mb-3">{t('domain.card.instructionsBody')}</p>
          </div>

          {/* 1. The CNAME for traffic — this is always shown. */}
          <DnsRecordRow
            type="CNAME"
            name={domain.hostname}
            value={cnameTarget}
            onCopy={onCopy}
            label={t('domain.card.labelCname')}
          />

          {/* 2. Ownership verification record from CF (only after add). */}
          {verification?.ownership && (
            <DnsRecordRow
              type={verification.ownership.type.toUpperCase()}
              name={verification.ownership.name}
              value={verification.ownership.value}
              onCopy={onCopy}
              label={t('domain.card.labelOwnership')}
            />
          )}

          {/* 3. SSL validation record (txt method, when CF returns one). */}
          {verification?.sslValidation?.name && verification?.sslValidation?.value && (
            <DnsRecordRow
              type="TXT"
              name={verification.sslValidation.name}
              value={verification.sslValidation.value}
              onCopy={onCopy}
              label={t('domain.card.labelSsl')}
            />
          )}

          {!verification && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              {t('domain.card.noCacheHint')}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            {isFailed && (
              <button
                onClick={onRevalidate}
                disabled={refreshing}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-medium text-sm transition-colors"
              >
                {t('domain.card.revalidate')}
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              {refreshing && <Loader2 className="w-4 h-4 animate-spin" />}
              {refreshing ? t('domain.card.checking') : t('domain.card.refreshNow')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DnsRecordRow: React.FC<{
  type: string;
  name: string;
  value: string;
  label: string;
  onCopy: (text: string, label: string) => void;
}> = ({ type, name, value, label, onCopy }) => {
  const { t } = useTranslation('tracking');
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
        {label}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-[80px_1fr_1fr_auto] gap-2 items-center text-sm">
        <span className="px-2 py-1 bg-gray-100 rounded font-mono text-xs text-gray-700 text-center">
          {type}
        </span>
        <code className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-900 truncate" title={name}>
          {name}
        </code>
        <code className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-900 truncate" title={value}>
          {value}
        </code>
        <button
          onClick={() => onCopy(value, t('domain.toast.copyValueLabel', { label }))}
          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
          title={t('domain.card.copyTitle')}
        >
          <Copy className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    </div>
  );
};
